// AppApiService.js - Fixed Version to Prevent Frequent Logouts in Production

import { getEnv } from "../config/env";
import { resolveQueryThreshold } from "../utils";

class AppApiService {
	constructor() {
		// API Configuration
		const env = getEnv();
		this.baseUrl = env.REACT_APP_API_URL;
		this.proxyUrl = env.REACT_APP_PROXY;
		this.apiUrl = this.proxyUrl; // Use proxy by default to avoid CORS
		this.timeout = 300000;

		// Cache Management
		this.filtersCache = new Map();
		this.filtersCacheTimeout = 10 * 60 * 1000; // 10 minutes

		// Track authentication state to prevent multiple redirects - ENHANCED
		this.isRedirecting = false;
		this.lastTokenCheck = 0;
		this.tokenCheckInterval = 300000; // INCREASED to 5 minutes to reduce aggressive checking
		this._lastAuthResult = undefined;
		this._redirectCooldown = 0;
		this.redirectCooldownPeriod = 30000; // 30 second cooldown between redirects

		console.log("📱 AppApiService initializing (Fixed Production Mode)...");
	}
	// ==================== ENHANCED AUTHENTICATION & REDIRECT METHODS ====================

	/**
	 * FIXED: More conservative redirect logic with multiple safeguards
	 */
	redirectToLogin() {
		const now = Date.now();

		// SAFEGUARD 1: Prevent multiple simultaneous redirects
		if (this.isRedirecting) {
			console.log("🔒 Redirect already in progress, skipping...");
			return;
		}

		// SAFEGUARD 2: Cooldown period between redirects
		if (now - this._redirectCooldown < this.redirectCooldownPeriod) {
			console.log(
				"🔒 Redirect cooldown active, skipping redirect for",
				Math.ceil(
					(this.redirectCooldownPeriod - (now - this._redirectCooldown)) / 1000,
				),
				"seconds",
			);
			return;
		}

		// SAFEGUARD 3: Don't redirect if we just checked recently (prevent redirect loops)
		const timeSinceLastCheck = now - this.lastTokenCheck;
		if (timeSinceLastCheck < 10000) {
			// Increased to 10 seconds
			console.log(
				"🔒 Recent token check, delaying redirect to prevent loops...",
			);
			return;
		}

		// SAFEGUARD 4: Additional token validation before redirect
		const token = this.getSharedAuthToken();
		if (token && this.isTokenActuallyExpired(token)) {
			console.log("🔒 Token confirmed expired, proceeding with redirect...");
		} else if (token) {
			console.log("🔒 Token still appears valid, skipping redirect");
			return;
		}

		this.isRedirecting = true;
		this._redirectCooldown = now;
		console.log("🔒 Token expired, redirecting to login page...");

		// Clear expired tokens
		this.clearExpiredTokens();

		// Try to use main ApiService logout if available
		if (window.apiService && typeof window.apiService.signOut === "function") {
			try {
				window.apiService.signOut();
			} catch (error) {
				console.warn("⚠️ Main ApiService signOut failed:", error);
			}
		}

		// Redirect to login page with a small delay to prevent rapid redirects
		setTimeout(() => {
			if (typeof window !== "undefined") {
				if (
					window.location.pathname !== "/" &&
					window.location.pathname !== "/login"
				) {
					window.location.href = "/";
				}
			}
			// Reset redirect flag after a delay
			setTimeout(() => {
				this.isRedirecting = false;
			}, 5000);
		}, 1000);
	}

	/**
	 * FIXED: More accurate token expiration check
	 */
	isTokenActuallyExpired(token) {
		if (!token) return true;

		try {
			const payload = this.parseJWT(token);
			if (!payload || !payload.exp) {
				console.warn("⚠️ Token has no expiration data");
				return false; // Don't assume expired if we can't parse
			}

			const now = Date.now();
			const expiration = payload.exp * 1000;
			const isActuallyExpired = expiration <= now;

			if (isActuallyExpired) {
				console.log(
					"🔒 Token is actually expired by",
					Math.floor((now - expiration) / 1000),
					"seconds",
				);
				return true;
			}

			const timeUntilExpiry = expiration - now;
			console.log(
				"✅ Token still valid for",
				Math.floor(timeUntilExpiry / 1000),
				"seconds",
			);
			return false;
		} catch (error) {
			console.warn("⚠️ Token validation failed:", error);
			return false; // Be lenient on parse errors
		}
	}

	/**
	 * FIXED: More lenient token expiration check with larger buffer
	 */
	checkTokenExpiration(token) {
		if (!token) return false;

		try {
			const payload = this.parseJWT(token);
			if (!payload || !payload.exp) {
				console.warn("⚠️ Token has no expiration data");
				return true; // Assume valid if we can't validate expiration
			}

			const now = Date.now();
			const expiration = payload.exp * 1000;
			const isExpired = expiration <= now;

			// FIXED: Reduced buffer time to 30 seconds to prevent premature logouts
			const expirationBuffer = 30 * 1000; // 30 seconds instead of 1 minute
			const isNearExpiry = expiration <= now + expirationBuffer;

			if (isExpired) {
				console.log("🔒 Token has actually expired");
				return false;
			}

			if (isNearExpiry) {
				console.log(
					"⚠️ Token will expire soon, but still valid for:",
					Math.floor((expiration - now) / 1000),
					"seconds",
				);
				return true; // FIXED: Still consider valid even if near expiry
			}

			return true;
		} catch (error) {
			console.warn("⚠️ Token validation failed:", error);
			return true; // FIXED: Be more lenient on parse errors
		}
	}

	setProxyUrl(url) {
		const normalized = url.replace(/\/$/, "");
		this.proxyUrl = normalized;
		this.apiUrl = normalized;
	}

	/**
	 * FIXED: Enhanced authentication check with better caching and less aggressive validation
	 */
	isAuthenticated() {
		const now = Date.now();

		// FIXED: More aggressive caching to reduce token checks
		if (now - this.lastTokenCheck < this.tokenCheckInterval) {
			if (this._lastAuthResult !== undefined) {
				// console.log('🔍 Using cached auth result:', this._lastAuthResult);
				return this._lastAuthResult;
			}
		}

		// Don't update lastTokenCheck too frequently
		if (now - this.lastTokenCheck > 60000) {
			// Only update every minute
			this.lastTokenCheck = now;
		}

		// FIXED: Check main ApiService first and trust its result more
		if (
			window.apiService &&
			typeof window.apiService.isAuthenticated === "function"
		) {
			try {
				const mainServiceAuth = window.apiService.isAuthenticated();
				if (mainServiceAuth) {
					this._lastAuthResult = true;
					return true;
				}
				// If main service says false, still check token but be more lenient
			} catch (error) {
				console.warn("⚠️ Main ApiService auth check failed:", error);
			}
		}

		// FIXED: More lenient token validation
		const token = this.getSharedAuthToken();
		if (!token) {
			console.log("🔒 No token available");
			this._lastAuthResult = false;
			return false;
		}

		// FIXED: Use less strict token validation
		const isValid = this.checkTokenExpiration(token);

		// FIXED: Cache positive results longer
		if (isValid) {
			this._lastAuthResult = true;
			this.lastTokenCheck = now; // Update timestamp on successful validation
		} else {
			// Don't immediately cache negative results - give it another chance
			console.log("🔒 Token validation failed, will retry on next check");
		}

		return isValid;
	}

	/**
	 * Get authentication token from the main ApiService
	 */
	getSharedAuthToken() {
		// Try to get token from main ApiService first
		if (
			window.apiService &&
			window.apiService.tokens &&
			window.apiService.tokens.idToken
		) {
			return window.apiService.tokens.idToken;
		}

		// Fallback to localStorage
		return (
			localStorage.getItem("google_id_token") ||
			localStorage.getItem("authToken")
		);
	}

	/**
	 * Clear expired tokens from storage
	 */
	clearExpiredTokens() {
		try {
			localStorage.removeItem("google_id_token");
			localStorage.removeItem("authToken");
			localStorage.removeItem("refresh_token");

			// Clear any other auth-related items
			const authKeys = Object.keys(localStorage).filter(
				(key) =>
					key.includes("auth") ||
					key.includes("token") ||
					key.includes("session"),
			);

			authKeys.forEach((key) => {
				localStorage.removeItem(key);
			});

			console.log("🧹 Expired tokens cleared from storage");
		} catch (error) {
			console.warn("⚠️ Failed to clear tokens:", error);
		}
	}

	/**
	 * Get user info from main ApiService
	 */
	getUser() {
		// Try main ApiService first
		if (window.apiService && typeof window.apiService.getUser === "function") {
			try {
				return window.apiService.getUser();
			} catch (error) {
				console.warn("⚠️ Main ApiService getUser failed:", error);
			}
		}

		// Fallback to parsing token manually
		const token = this.getSharedAuthToken();
		if (!token) return null;

		try {
			const payload = this.parseJWT(token);
			return payload
				? {
						id: payload.sub,
						email: payload.email,
						name: payload.name,
						picture: payload.picture,
						email_verified: payload.email_verified,
					}
				: null;
		} catch (error) {
			console.warn("⚠️ Token parsing failed:", error);
			return null;
		}
	}

	/**
	 * Parse JWT token with better error handling
	 */
	parseJWT(token) {
		try {
			if (!token || typeof token !== "string") {
				return null;
			}

			const parts = token.split(".");
			if (parts.length !== 3) {
				console.warn("⚠️ Invalid JWT format");
				return null;
			}

			const base64Url = parts[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch (error) {
			console.warn("⚠️ JWT parsing error:", error);
			return null;
		}
	}

	/**
	 * FIXED: More lenient authentication requirement
	 */
	async ensureAuthenticated() {
		// FIXED: Don't be too aggressive about authentication
		const token = this.getSharedAuthToken();

		if (!token) {
			console.log("🔒 No token available, redirecting to login...");
			this.redirectToLogin();
			return new Promise(() => {});
		}

		// FIXED: Check if token is actually expired, not just near expiry
		if (!this.isTokenActuallyExpired(token)) {
			return { success: true, message: "Authentication valid" };
		}

		console.log("🔒 Token actually expired, redirecting to login...");
		this.redirectToLogin();
		return new Promise(() => {});
	}

	async testConnectivity() {
		try {
			const res = await fetch(`${this.baseUrl || this.appDataApiUrl}/ping`, {
				method: "GET",
			});
			return res.ok;
		} catch (err) {
			console.error("testConnectivity failed:", err);
			return false;
		}
	}

	// ==================== FIXED REQUEST HEADERS & API CALLS ====================

	/**
	 * Get headers with shared authentication
	 */
	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		const token = this.getSharedAuthToken();
		// FIXED: Only add auth header if token exists and is not actually expired
		if (token && !this.isTokenActuallyExpired(token)) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	/**
	 * FIXED: More conservative auth error detection
	 */
	isActualAuthError(response, responseText) {
		// Be even more specific about what constitutes an auth error
		const authErrorIndicators = [
			"jwt expired",
			"token expired",
			"invalid token",
			"unauthorized",
			"authentication failed",
			"invalid_token",
			"token_expired",
			"authentication required",
			"jwt malformed",
		];

		const errorMessage = responseText.toLowerCase();
		const isAuthRelated = authErrorIndicators.some((indicator) =>
			errorMessage.includes(indicator),
		);

		// FIXED: Also verify the token is actually expired locally
		const token = this.getSharedAuthToken();
		const isTokenActuallyExpired = !token || this.isTokenActuallyExpired(token);

		// FIXED: More conservative - require strong evidence of auth failure
		const isDefiniteAuthError =
			isAuthRelated &&
			isTokenActuallyExpired &&
			(response.status === 401 || response.status === 403);

		console.log("🔍 Auth error analysis:", {
			isAuthRelated,
			isTokenActuallyExpired,
			responseStatus: response.status,
			isDefiniteAuthError,
		});

		return isDefiniteAuthError;
	}

	/**
	 * FIXED: Enhanced API request handling with better error classification
	 */
	async makeRequest(endpoint, options = {}) {
		try {
			// FIXED: Less aggressive initial auth check
			const token = this.getSharedAuthToken();
			if (!token) {
				console.log("🔒 No token available, redirecting to login...");
				this.redirectToLogin();
				return new Promise(() => {});
			}

			// FIXED: Don't pre-validate token expiration aggressively
			const headers = this.getHeaders();
			const normalizedEndpoint = endpoint.startsWith("/")
				? endpoint
				: `/${endpoint}`;
			const url = `${this.apiUrl}${normalizedEndpoint}`;

			const requestOptions = {
				method: "GET",
				...options,
				headers: { ...headers, ...options.headers },
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
			};

			console.log(`🌐 AppAPI Request: ${requestOptions.method} ${url}`);

			const response = await fetch(url, {
				...requestOptions,
				signal: AbortSignal.timeout(this.timeout),
			});

			// FIXED: Much more conservative handling of 401/403 errors
			if (response.status === 401 || response.status === 403) {
				const responseText = await response.text().catch(() => "");

				console.log("⚠️ Received 401/403, analyzing error type...");

				// FIXED: Only redirect if it's definitely an auth error
				if (this.isActualAuthError(response, responseText)) {
					console.log(
						"🔒 Confirmed authentication failure, redirecting to login...",
					);
					this.redirectToLogin();
					return new Promise(() => {});
				} else {
					console.warn(
						"⚠️ API returned 401/403 but appears to be validation error, not auth failure",
					);
					return {
						success: false,
						error: `API validation error (${response.status}): ${responseText}`,
						statusCode: response.status,
						isPayloadError: true,
					};
				}
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				console.warn(`⚠️ API Error (${response.status}):`, errorText);

				return {
					success: false,
					error: `HTTP ${response.status}: ${response.statusText}. ${errorText}`,
					statusCode: response.status,
					isPayloadError: response.status >= 400 && response.status < 500,
				};
			}

			const data = await response.json();
			console.log("✅ AppAPI Response received successfully");

			// FIXED: Reset auth state on successful API call
			this._lastAuthResult = true;
			this.lastTokenCheck = Date.now();

			return {
				success: true,
				data: data,
				statusCode: response.status,
			};
		} catch (error) {
			console.error("❌ AppAPI Error:", error);

			let errorMessage = error.message;
			let isPayloadError = false;

			if (
				error.name === "TypeError" &&
				error.message.includes("Failed to fetch")
			) {
				errorMessage =
					"Network error: Unable to connect to App API. Please check your internet connection and try again.";
			} else if (error.message.includes("CORS")) {
				errorMessage =
					"CORS error: App API access blocked. Please contact support.";
			} else if (
				error.message.includes("400") ||
				error.message.includes("422")
			) {
				isPayloadError = true;
				errorMessage = `Payload validation error: ${error.message}`;
			}

			return {
				success: false,
				error: errorMessage,
				statusCode: error.status || 500,
				isPayloadError,
			};
		}
	}

	// ==================== APP SEARCH METHOD ====================

	/**
	 * FIXED: Enhanced app search with better error handling
	 */
	async performAppSearch(
		searchTerms,
		filters = {},
		performanceMetric = "downloads",
		minValue = 1000,
		searchOptions = {},
	) {
		try {
			console.log("🔍 Performing app search with API:", {
				searchTerms,
				filters,
				performanceMetric,
				minValue,
			});

			// FIXED: More lenient initial auth check
			const token = this.getSharedAuthToken();
			if (!token) {
				return {
					success: false,
					error: "Authentication required",
					requiresAuth: true,
				};
			}

			// FIXED: Don't fail immediately on near-expiry tokens
			if (this.isTokenActuallyExpired(token)) {
				return {
					success: false,
					error: "Token expired",
					requiresAuth: true,
				};
			}

			// Separate include and exclude terms
			const includeTerms = searchTerms.filter(
				(term) => term.type === "include",
			);
			const excludeTerms = searchTerms.filter(
				(term) => term.type === "exclude",
			);

			// Build and validate the payload
			const payload = this.buildSearchPayload(
				includeTerms,
				excludeTerms,
				filters,
				performanceMetric,
				minValue,
				searchOptions,
			);

			// Validate payload before sending
			const validationResult = this.validateSearchPayload(payload);
			if (!validationResult.isValid) {
				console.error("❌ Payload validation failed:", validationResult.errors);
				return {
					success: false,
					error: `Payload validation failed: ${validationResult.errors.join(", ")}`,
					isPayloadError: true,
				};
			}

			console.log(
				"📡 Sending validated search payload:",
				JSON.stringify(payload, null, 2),
			);

			// Make the API call to the new endpoint
			const response = await this.makeRequest("app-data/search", {
				method: "POST",
				body: JSON.stringify(payload),
			});

			if (response.success) {
				console.log("✅ App search successful");
				return {
					success: true,
					data: response.data,
					statusCode: response.statusCode,
				};
			} else if (response.isPayloadError) {
				console.warn("⚠️ Payload validation error from server:", response.error);
				return {
					success: false,
					error: response.error,
					isPayloadError: true,
					statusCode: response.statusCode,
				};
			} else if (response.requiresAuth) {
				this.redirectToLogin();
				return new Promise(() => {});
			} else {
				throw new Error(response.error || "App search failed");
			}
		} catch (error) {
			console.error("❌ App search error:", error);
			return {
				success: false,
				error: error.message,
				statusCode: 500,
			};
		}
	}

	/**
	 * Validate search payload before sending to API
	 */
	validateSearchPayload(payload) {
		const errors = [];

		// Check required fields
		if (!payload.query) {
			errors.push("Query is required");
		}

		if (!payload.filters) {
			errors.push("Filters object is required");
		}

		// Validate query structure
		if (Array.isArray(payload.query)) {
			payload.query.forEach((q, index) => {
				if (!q.query || typeof q.query !== "string") {
					errors.push(`Query[${index}] must have a valid query string`);
				}
				if (
					typeof q.threshold !== "number" ||
					q.threshold < 0 ||
					q.threshold > 1
				) {
					errors.push(
						`Query[${index}] threshold must be a number between 0 and 1`,
					);
				}
			});
		}

		// Validate performance data
		if (
			payload.filters.performance &&
			Array.isArray(payload.filters.performance.performanceData)
		) {
			payload.filters.performance.performanceData.forEach((perf, index) => {
				if (!perf.filterColumn || typeof perf.filterColumn !== "string") {
					errors.push(`Performance[${index}] must have a valid filterColumn`);
				}
				if (typeof perf.filterThreshold !== "number") {
					errors.push(`Performance[${index}] filterThreshold must be a number`);
				}
			});
		}

		// Validate numeric fields
		if (
			payload.filters.noOfResult &&
			(typeof payload.filters.noOfResult !== "number" ||
				payload.filters.noOfResult <= 0)
		) {
			errors.push("noOfResult must be a positive number");
		}

		if (
			payload.filters.noOfDays &&
			(typeof payload.filters.noOfDays !== "number" ||
				payload.filters.noOfDays <= 0)
		) {
			errors.push("noOfDays must be a positive number");
		}

		const validSortOrders = ["asc", "desc"];
		if (payload.view && !validSortOrders.includes(payload.view)) {
			errors.push(
				`view (sort order) must be one of: ${validSortOrders.join(", ")}`,
			);
		}

		return {
			isValid: errors.length === 0,
			errors,
		};
	}

	// [Keep all existing methods: buildSearchPayload, getFilterValues, etc. - they remain unchanged]
	buildSearchPayload(
		includeTerms,
		excludeTerms,
		filters,
		performanceMetric,
		minValue,
		searchOptions,
	) {
		console.log("🔧 Building search payload with field mappings and sorting");
		console.log("📥 Input filters received:", filters);
		console.log("📥 Input searchOptions received:", searchOptions);

		// Convert search terms to API format with validation
		const query = includeTerms
			.map((term) => ({
				query: String(term.text || "").trim(),
				threshold: resolveQueryThreshold(term, "APPS"),
			}))
			.filter((q) => q.query.length > 0); // Remove empty queries

		const excludeQuery = excludeTerms
			.map((term) => ({
				query: String(term.text || "").trim(),
				threshold: resolveQueryThreshold(term, "APPS"),
			}))
			.filter((q) => q.query.length > 0); // Remove empty queries

		const noOfResult = Math.max(
			1,
			parseInt(
				filters.noOfResult ||
					filters.limit ||
					searchOptions.noOfResult ||
					searchOptions.limit ||
					1000,
			),
		);

		// Combine filters and searchOptions for comprehensive extraction
		const allFilters = { ...filters, ...searchOptions };

		// Extract sorting options from combined filters
		const sortBy =
			allFilters.sortBy || searchOptions.sortBy || "compositeScore";
		const sortOrder =
			allFilters.sortBy === "score"
				? allFilters.sortOrder === "asc"
					? "desc"
					: "asc"
				: allFilters.sortOrder ||
					allFilters.view ||
					searchOptions.sortOrder ||
					"desc";

		console.log("🔧 Extracted sorting options for payload:", {
			sortBy: sortBy,
			sortOrder: sortOrder,
		});

		// Extract custom filters - these should override default performance settings
		const customFilters =
			allFilters.customFilters || allFilters.performanceFilters || [];
		let performanceDataArray = [];

		if (Array.isArray(customFilters) && customFilters.length > 0) {
			performanceDataArray = customFilters
				.map((filter) => ({
					filterColumn: String(
						filter.column || filter.filterColumn || "cprmarketrequests",
					),
					filterThreshold: parseFloat(
						filter.threshold || filter.filterThreshold || filter.value || 0,
					),
				}))
				.filter((p) => !isNaN(p.filterThreshold)); // Remove invalid thresholds
		} else {
			// Fallback to default performance mapping
			const performanceColumnMap = {
				impressions: "cprmarketrequests",
				viewability: "cprmarketrequests",
				ctr: "cprmarketrequests",
				vtr: "cprmarketrequests",
				reach: "cprmarketrequests",
			};

			const filterColumn =
				performanceColumnMap[performanceMetric] || "cprmarketrequests";
			const filterThreshold = parseFloat(minValue) || 0.0;

			performanceDataArray = [
				{
					filterColumn,
					filterThreshold,
				},
			];
		}

		// Map UI performance metrics to API field names for cumulative data
		const getCumulativeDataKey = (metric) => {
			const mapping = {
				viewability: "avg_viewability",
				ctr: "avg_clickthroughrate",
				vtr: "avg_vtr",
				reach: "total_cprmarketrequests",
				impressions: "total_impressions",
			};
			return mapping[metric] || "avg_clickthroughrate";
		};

		// Get threshold values from filters
		const getThresholdValues = (filters) => {
			const minReachDay = parseFloat(minValue) || 1;
			const reachThresholdValue = parseFloat(minReachDay * 1000);

			return {
				reachThreashold: 0,
				marketThreashold: Math.max(0, reachThresholdValue),
			};
		};

		// Get period days from filters with validation
		const getPeriodDays = (filters) => {
			if (filters.period) {
				if (filters.period === "7 days" || filters.period === 7) return 7;
				if (filters.period === "30 days" || filters.period === 30) return 30;
				const parsed = parseInt(filters.period);
				return !isNaN(parsed) && parsed > 0 ? parsed : 30;
			}
			if (filters.noOfDays) {
				const parsed = parseInt(filters.noOfDays);
				return !isNaN(parsed) && parsed > 0 ? parsed : 30;
			}
			if (filters.dateRange) {
				const parsed = parseInt(filters.dateRange);
				return !isNaN(parsed) && parsed > 0 ? parsed : 30;
			}
			return 30;
		};

		// Extract app-specific filters with correct field mappings
		const appSubcategory = [];
		const developers = allFilters.developer || allFilters.developers || [];
		const contentRating = allFilters.contentRating || [];
		const appKeywords = allFilters.appKeywords || allFilters.keywords || [];
		const rating =
			allFilters.rating || allFilters.appRating || allFilters.minRating || null;
		const ratingCount =
			allFilters.rating_count || allFilters.minRatingCount || 0;
		const downloads =
			allFilters.downloads ||
			allFilters.downloadRanges ||
			allFilters.appDownloads ||
			null;
		const platform = allFilters.platform || [];
		const marketSource = allFilters.marketSource || [];
		const appCategories = allFilters.category || allFilters.categories || [];

		// Handle app pricing with correct field mapping
		const appPricing =
			allFilters.isPaid !== undefined ? allFilters.isPaid : allFilters.paid;
		const coppaValue = allFilters.coppa;
		const languages = allFilters.languages || [];
		const regions = allFilters.regions || allFilters.appRegions || [];
		const appNames = allFilters.appNames || allFilters.app_names || [];
		const bundleIds = allFilters.bundleIds || allFilters.bundle_ids || [];

		// Extract domain filters with correct field mappings
		const geography =
			allFilters.geography ||
			allFilters.countries ||
			allFilters.userGeoCountries ||
			[];
		const deviceTypes =
			allFilters.deviceTypes ||
			allFilters.device_types ||
			allFilters.deviceType ||
			[];
		const mediaSubtype =
			allFilters.mediaSubtype ||
			allFilters.mediaSubtypes ||
			allFilters.media_subtype ||
			[];
		const videoplcmt =
			allFilters.videoplcmt ||
			allFilters.videoPlacement ||
			allFilters.videoPlacementTypes ||
			allFilters.video_placement ||
			[];
		const distributionChannel =
			allFilters.distributionChannel || allFilters.distributionChannels || [];
		const contentStyle =
			allFilters.contentStyle || allFilters.contentStyles || [];
		const isDirect = allFilters.isDirect;

		// Extract uploaded domain filters
		const uploadedBundles = allFilters.uploadedBundles || {};
		const includeBundles = uploadedBundles.includeList || [];
		const excludeBundles = uploadedBundles.excludeList || [];

		// Extract other options with validation
		const startIndex = Math.max(0, parseInt(allFilters.startIndex) || 0);
		const noOfDays = getPeriodDays(allFilters);
		const publisher = allFilters.publisher || allFilters.publishers || [];
		const tier = Math.max(0, parseInt(allFilters.tier) || 0);
		const minViewability = parseInt(
			allFilters.minViewability || allFilters.viewabilityThreshold || 0,
		);
		const minReach = Math.max(
			0,
			parseFloat(allFilters.minReach || allFilters.reachThreshold) || 0,
		);

		// Helper functions with validation
		const ensureArray = (value) => {
			if (Array.isArray(value))
				return value.filter((v) => v !== null && v !== undefined && v !== "");
			if (value === null || value === undefined || value === "") return [];
			return [value];
		};

		const parseNumeric = (value) => {
			if (value === null || value === undefined || value === "") return null;
			const parsed = parseFloat(value);
			return isNaN(parsed) ? null : parsed;
		};

		const parseCoppaValue = (value) => {
			if (value === true || value === "true") return true;
			if (value === false || value === "false") return false;
			return null;
		};

		const parseAppPricing = (value) => {
			if (value === true || value === "paid" || value === "Paid") {
				return true;
			}
			if (value === false || value === "free" || value === "Free") {
				return false;
			}
			return null;
		};

		// Parse the app pricing value
		const parsedAppPricing = parseAppPricing(appPricing);

		// Build payload with validation
		const payload = {
			query,
			excludeQuery,
			filters: {
				performance: {
					minViewability: parseNumeric(minViewability) || 0,
					minReach: parseNumeric(minReach) || 0,
					performanceData: performanceDataArray,
				},
				domainFilters: {
					geography:
						ensureArray(geography).length > 0 ? ensureArray(geography) : [],
					contentStyle: ensureArray(contentStyle),
					videoplcmt: ensureArray(videoplcmt),
					distributionChannel: ensureArray(distributionChannel),
					deviceTypes: ensureArray(deviceTypes),
					mediaSubtype: ensureArray(mediaSubtype),
					publisher: ensureArray(publisher),
					isDirect: isDirect,
				},
				appfilters: {
					subcategory: ensureArray(appSubcategory),
					developer: ensureArray(developers),
					content_rating: ensureArray(contentRating),
					keywords: ensureArray(appKeywords),
					rating: parseNumeric(rating),
					rating_count: parseNumeric(ratingCount),
					downloads: parseNumeric(downloads),
					update_date: null,
					languages: ensureArray(languages),
					regions: ensureArray(regions),
					market_source: ensureArray(marketSource),
					market_status: null,
					name: [],
					paid: parsedAppPricing,
					origin: [],
					creator: [],
					category: ensureArray(appCategories),
					platform: ensureArray(platform),
					appinfo_content_rating: ensureArray(contentRating),
					coppa: parseCoppaValue(coppaValue),
					deleted: null,
					modified_datetime: null,
				},
				noOfResult: noOfResult,
				noOfDays: noOfDays,
				tier_Details: {
					tier: tier,
					keys: [],
				},
				cumulativeDataFilters: {
					perfomanceData: getCumulativeDataKey(performanceMetric),
					...getThresholdValues(allFilters),
				},
				includeBundles: ensureArray(includeBundles),
				excludeBundles: ensureArray(excludeBundles),
			},
			sortBy: sortBy,
			view: sortOrder,
		};

		console.log("✅ Search payload built and validated successfully");
		return payload;
	}

	// ==================== APP FILTER VALUES METHOD ====================

	/**
	 * FIXED: Enhanced filter values fetch with better error handling
	 */
	async getFilterValues() {
		try {
			console.log("📱 Fetching app filter values (on-demand)...");

			// FIXED: More lenient auth check
			const token = this.getSharedAuthToken();
			if (!token) {
				return {
					success: false,
					error: "Authentication required",
					requiresAuth: true,
				};
			}

			// FIXED: Don't fail on near-expiry tokens
			if (this.isTokenActuallyExpired(token)) {
				return {
					success: false,
					error: "Token expired",
					requiresAuth: true,
				};
			}

			// Check cache first
			const user = this.getUser();
			const cacheKey = user
				? `app_filter_values_${user.id}`
				: "app_filter_values_anonymous";
			const cached = this.filtersCache.get(cacheKey);

			if (cached && Date.now() - cached.timestamp < this.filtersCacheTimeout) {
				console.log("📋 Using cached app filter values");
				return {
					success: true,
					appFilterValues: cached.data,
					cached: true,
				};
			}

			// Make authenticated request
			const response = await this.makeRequest(
				"/domain-filters/get-filter-values?type=app",
			);

			if (response.success) {
				const filterData = response.data;
				const appFilterValues =
					this.extractAndProcessAppFilterValues(filterData);

				// Cache the processed values
				this.filtersCache.set(cacheKey, {
					data: appFilterValues,
					timestamp: Date.now(),
				});

				console.log("🎯 App filter values extracted and cached:", {
					categoriesCount: appFilterValues.categories?.length || 0,
					developersCount: appFilterValues.developers?.length || 0,
					publishersCount: appFilterValues.publishers?.length || 0,
					platformsCount: appFilterValues.platforms?.length || 0,
					contentRatingsCount: appFilterValues.contentRatings?.length || 0,
					countriesCount: appFilterValues.countries?.length || 0,
				});

				return {
					success: true,
					appFilterValues,
					//domainFilterValues: filterData,
					rawData: filterData,
					cached: false,
					authenticated: this.isAuthenticated(),
				};
			} else if (response.requiresAuth) {
				this.redirectToLogin();
				return new Promise(() => {});
			}

			// If API call failed but not auth issue, return fallback data
			console.warn("⚠️ App API call failed, using fallback data");
			const fallbackData = this.getDefaultAppFilterValues();

			return {
				success: true,
				appFilterValues: fallbackData,
				fallback: true,
				error: response.error,
				authenticated: this.isAuthenticated(),
			};
		} catch (error) {
			console.error("❌ App filter values fetch failed:", error);

			const fallbackData = this.getDefaultAppFilterValues();

			return {
				success: true,
				appFilterValues: fallbackData,
				fallback: true,
				error: error.message,
				authenticated: this.isAuthenticated(),
			};
		}
	}

	// [Keep all remaining methods unchanged - they don't need modification for this issue]

	extractAndProcessAppFilterValues(responseData) {
		console.log("🔍 Extracting app filter values from API response...");

		const appFilterValues = responseData.appFilterValues || {};

		// Helper function to clean and deduplicate arrays
		const cleanArray = (arr) => {
			if (!Array.isArray(arr)) return [];
			return [
				...new Set(
					arr.filter(
						(item) =>
							item !== null &&
							item !== undefined &&
							item !== "" &&
							typeof item === "string" &&
							item.trim() !== "",
					),
				),
			].sort();
		};

		// Helper function to parse JSON strings in arrays
		const parseJSONStrings = (arr) => {
			if (!Array.isArray(arr)) return [];
			const parsed = [];
			arr.forEach((item) => {
				if (
					typeof item === "string" &&
					item.startsWith("[") &&
					item.endsWith("]")
				) {
					try {
						const jsonArray = JSON.parse(item);
						if (Array.isArray(jsonArray)) {
							parsed.push(...jsonArray);
						}
					} catch {
						parsed.push(item);
					}
				} else if (item !== null && item !== undefined && item !== "") {
					parsed.push(item);
				}
			});
			return cleanArray(parsed);
		};

		const processed = {
			// Targeting Filters
			mediaSubtypes: cleanArray(
				appFilterValues.iabMediaSubtypes || [
					"Display",
					"Video",
					"Native",
					"Display+Video",
					"Display+Native",
					"Video+Native",
				],
			),

			deviceTypes: cleanArray(
				appFilterValues.deviceTypes || [
					"phone",
					"tablet",
					"desktop",
					"connected tv",
					"connected device",
					"mobile/tablet",
					"set-top-box",
					"dooh",
				],
			),

			videoPlacementTypes: cleanArray(
				appFilterValues.videoPlacements || [
					"INSTREAM",
					"OUTSTREAM",
					"REWARDED",
					"INTERSTITIAL",
					"ACCOMPANYING_CONTENT",
					"NO_CONTENT_STANDALONE",
				],
			),

			// Geography
			countries: cleanArray(appFilterValues.userGeoCountries || []),
			regions: appFilterValues.regions || [],

			// App Store Information
			appStores: cleanArray([
				"Google Play Store",
				"Apple App Store",
				"Amazon Appstore",
			]),
			platforms: cleanArray(
				appFilterValues.appinfo_platform || ["android", "ios"],
			),
			marketSources: cleanArray(
				appFilterValues.market_source || ["playstore", "itunes", "appinfo"],
			),
			marketStatuses: cleanArray(
				appFilterValues.market_status || ["PUBLISHED", "pending"],
			),
			distributionChannels: cleanArray(
				appFilterValues.iabDistributionChannels || [],
			),

			// App Metadata
			developers: cleanArray(appFilterValues.developer || []),
			appNames: cleanArray(appFilterValues.appinfo_name || []),
			bundleIds: cleanArray(appFilterValues.bundle_id || []),

			contentRatings: cleanArray([
				...(appFilterValues.content_rating || []),
				...(appFilterValues.appinfo_content_rating || []),
			]),

			categories: parseJSONStrings([
				...(appFilterValues.appinfo_category || []),
				...(appFilterValues.subcategory || []),
			]),

			languages: parseJSONStrings(appFilterValues.languages || []),
			publishers: cleanArray(appFilterValues.publisher || []),

			// Performance Data
			ratings: (appFilterValues.rating || []).filter(
				(r) => r !== null && !isNaN(r),
			),
			ratingCounts: (appFilterValues.rating_count || []).filter(
				(r) => r !== null && !isNaN(r),
			),
			downloads: (appFilterValues.downloads || []).filter(
				(d) => d !== null && !isNaN(d),
			),

			// Keywords and other data
			keywords: cleanArray(appFilterValues.keywords || []),
			origins: cleanArray(appFilterValues.appinfo_origin || []),
			domains: cleanArray(appFilterValues.appinfo_domain || []),

			// Available columns for custom filters
			availableColumns: this.getAvailableColumns(),
		};

		// Calculate derived data
		processed.downloadRanges = this.calculateDownloadRanges(
			processed.downloads,
		);
		processed.ratingRanges = this.calculateRatingRanges(processed.ratings);

		console.log("✅ App filter values processed:", {
			categories: processed.categories.length,
			developers: processed.developers.length,
			publishers: processed.publishers.length,
			platforms: processed.platforms.length,
			contentRatings: processed.contentRatings.length,
			countries: processed.countries.length,
		});

		return processed;
	}

	getAvailableColumns() {
		return [
			{ value: "cprmarketrequests", label: "CPR Market Requests" },
			{ value: "impressions", label: "Total Impressions" },
			{ value: "clicks", label: "Clicks" },
			{ value: "viewables", label: "Viewables" },
			{ value: "viewabilitymeasures", label: "Viewability Measures" },
			{ value: "completefirstoccurrence", label: "Video Completions" },
			{ value: "startfirstoccurrence", label: "Video Starts" },
			{ value: "cpr", label: "CPR" },
			{ value: "fills", label: "Fills" },
			{ value: "nonzerobids", label: "Non Zero Bids" },
			{ value: "dealvolumedealinfo", label: "Deal Volume Deal Info" },
			{ value: "nonzerobidsvalueinusd", label: "Non Zero Bids Value (USD)" },
			{ value: "bidspendinusd", label: "Bid Spend (USD)" },
			{ value: "ctr", label: "CTR" },
			{ value: "vtr", label: "VTR" },
			{ value: "revenueperimpression", label: "Revenue Per Impression" },
			{ value: "costperimpression", label: "Cost Per Impression" },
			{ value: "bid_guidance", label: "Bid Guidance" },
			{ value: "bidCpm", label: "CPM" },
			{
				value: "impressionswithclickmacro",
				label: "Impressions with Click Macro",
			},
			{ value: "cprmarketdemand", label: "CPR Market Demand" },
			{ value: "partnerspendinusd", label: "Partner Spend (USD)" },
			{
				value: "cprmarketpublisherrevenueinusd",
				label: "CPR Market Publisher Revenue (USD)",
			},
			{ value: "cprallrequests", label: "CPR All Requests" },
			{
				value: "cprprivatemarketimpressions",
				label: "CPR Private Market Impressions",
			},
		];
	}

	calculateDownloadRanges(downloads) {
		if (!Array.isArray(downloads) || downloads.length === 0) {
			return [
				"1-100",
				"100-1K",
				"1K-10K",
				"10K-50K",
				"50K-100K",
				"100K-1M",
				"1M-10M",
				"10M-50M",
				"50M-100M",
				"100M+",
			];
		}

		const ranges = new Set();
		downloads.forEach((download) => {
			if (download >= 100000000) ranges.add("100M+");
			else if (download >= 50000000) ranges.add("50M-100M");
			else if (download >= 10000000) ranges.add("10M-50M");
			else if (download >= 1000000) ranges.add("1M-10M");
			else if (download >= 100000) ranges.add("100K-1M");
			else if (download >= 50000) ranges.add("50K-100K");
			else if (download >= 10000) ranges.add("10K-50K");
			else if (download >= 1000) ranges.add("1K-10K");
			else if (download >= 100) ranges.add("100-1K");
			else ranges.add("1-100");
		});

		return Array.from(ranges).sort();
	}

	calculateRatingRanges(ratings) {
		if (!Array.isArray(ratings) || ratings.length === 0) {
			return ["1.0-2.0", "2.0-3.0", "3.0-4.0", "4.0-5.0"];
		}

		const ranges = new Set();
		ratings.forEach((rating) => {
			if (rating >= 4.0) ranges.add("4.0-5.0");
			else if (rating >= 3.0) ranges.add("3.0-4.0");
			else if (rating >= 2.0) ranges.add("2.0-3.0");
			else ranges.add("1.0-2.0");
		});

		return Array.from(ranges).sort();
	}

	getDefaultAppFilterValues() {
		console.log("⚠️ Using default app filter values (API fallback)");

		return {
			mediaSubtypes: [
				"Display",
				"Video",
				"Native",
				"Display+Video",
				"Display+Native",
				"Video+Native",
			],
			deviceTypes: [
				"phone",
				"tablet",
				"desktop",
				"connected tv",
				"connected device",
				"mobile/tablet",
				"set-top-box",
				"dooh",
			],
			videoPlacementTypes: [
				"INSTREAM",
				"OUTSTREAM",
				"REWARDED",
				"INTERSTITIAL",
				"ACCOMPANYING_CONTENT",
				"NO_CONTENT_STANDALONE",
			],
			appStores: ["Google Play Store", "Apple App Store", "Amazon Appstore"],
			platforms: ["android", "ios"],
			marketSources: ["playstore", "itunes", "appinfo"],
			marketStatuses: ["PUBLISHED", "pending"],
			distributionChannels: ["App", "Channel"],
			developers: [
				"Google LLC",
				"Apple Inc.",
				"Microsoft Corporation",
				"Meta Platforms Inc.",
			],
			appNames: [],
			bundleIds: [],
			contentRatings: [
				"4+",
				"+12",
				"Teen",
				"General",
				"Medium Maturity",
				"Everyone",
				"Everyone 10+",
				"Mature 17+",
			],
			categories: [
				"Health & Fitness",
				"Productivity",
				"Games",
				"Entertainment",
				"Lifestyle",
				"Utilities",
				"Business",
			],
			languages: [
				"English",
				"Spanish",
				"French",
				"German",
				"Italian",
				"Portuguese",
				"Chinese",
				"Japanese",
			],
			publishers: [],
			countries: ["US", "GB", "CA", "AU", "DE", "FR", "JP", "KR", "CN", "IN"],
			regions: [],
			downloadRanges: [
				"1-100",
				"100-1K",
				"1K-10K",
				"10K-50K",
				"50K-100K",
				"100K-1M",
				"1M-10M",
				"10M-50M",
				"50M-100M",
				"100M+",
			],
			ratingRanges: ["1.0-2.0", "2.0-3.0", "3.0-4.0", "4.0-5.0"],
			availableColumns: this.getAvailableColumns(),
			ratings: [],
			ratingCounts: [],
			downloads: [],
			keywords: [],
			origins: [],
			domains: [],
		};
	}

	// async getAppKeywords(query) {
	//   try {
	//     if (!query || query.length < 2) {
	//       return { success: true, keywords: [] };
	//     }

	//     const token = this.getSharedAuthToken();
	//     if (!token) {
	//       return { success: true, keywords: [], requiresAuth: true };
	//     }

	//     const response = await this.makeRequest(`/app-keywords/suggest?q=${encodeURIComponent(query)}`);

	//     if (response.success && response.data) {
	//       return {
	//         success: true,
	//         keywords: response.data.keywords || []
	//       };
	//     } else if (response.requiresAuth) {
	//       this.redirectToLogin();
	//       return new Promise(() => { });
	//     }

	//     return { success: true, keywords: [] };
	//   } catch (error) {
	//     console.warn('⚠️ App keywords suggestion failed:', error);
	//     return { success: true, keywords: [] };
	//   }
	// }

	// ==================== ENHANCED UTILITY METHODS ====================

	getAuthStatus() {
		const mainServiceAuth = window.apiService
			? {
					available: true,
					authenticated: window.apiService.isAuthenticated
						? window.apiService.isAuthenticated()
						: false,
					user: window.apiService.getUser ? window.apiService.getUser() : null,
				}
			: { available: false };

		return {
			isAuthenticated: this.isAuthenticated(),
			user: this.getUser(),
			hasSharedToken: !!this.getSharedAuthToken(),
			mainApiService: mainServiceAuth,
			lastTokenCheck: this.lastTokenCheck,
			isRedirecting: this.isRedirecting,
			redirectCooldown: this._redirectCooldown,
			tokenCheckInterval: this.tokenCheckInterval,
		};
	}

	clearFilterCache() {
		this.filtersCache.clear();
		console.log("🧹 App filter cache cleared");
	}

	getCacheStatus() {
		const cacheEntries = Array.from(this.filtersCache.entries()).map(
			([key, value]) => ({
				key,
				age: Date.now() - value.timestamp,
				size: JSON.stringify(value.data).length,
			}),
		);

		return {
			entries: cacheEntries.length,
			details: cacheEntries,
		};
	}

	/**
	 * ENHANCED: Reset authentication state with more comprehensive cleanup
	 */
	resetAuthState() {
		this.isRedirecting = false;
		this.lastTokenCheck = 0;
		this._lastAuthResult = undefined;
		this._redirectCooldown = 0;
		console.log("🔄 Authentication state reset completely");
	}

	/**
	 * ADDED: Force refresh authentication state
	 */
	forceAuthRefresh() {
		this._lastAuthResult = undefined;
		this.lastTokenCheck = 0;
		const isAuth = this.isAuthenticated();
		console.log("🔄 Forced auth refresh result:", isAuth);
		return isAuth;
	}

	/**
	 * ADDED: Debug method to check token details
	 */
	debugTokenInfo() {
		const token = this.getSharedAuthToken();
		if (!token) {
			console.log("🔍 No token found");
			return { hasToken: false };
		}

		try {
			const payload = this.parseJWT(token);
			const now = Date.now();
			const exp = payload?.exp ? payload.exp * 1000 : null;

			return {
				hasToken: true,
				tokenLength: token.length,
				isValidFormat: !!payload,
				expiration: exp,
				timeUntilExpiry: exp ? Math.floor((exp - now) / 1000) : null,
				isActuallyExpired: exp ? exp <= now : null,
				userInfo: payload
					? {
							sub: payload.sub,
							email: payload.email,
							name: payload.name,
						}
					: null,
			};
		} catch (error) {
			return {
				hasToken: true,
				tokenLength: token.length,
				parseError: error.message,
			};
		}
	}
}

// Create and export singleton instance
const appApiService = new AppApiService();

// Enhanced global access for debugging
if (typeof window !== "undefined") {
	window.appApiService = appApiService;

	// Add debug helpers
	window.debugAppAuth = () => {
		console.log("🔍 App API Service Debug Info:");
		console.log("Auth Status:", appApiService.getAuthStatus());
		console.log("Token Info:", appApiService.debugTokenInfo());
		console.log("Cache Status:", appApiService.getCacheStatus());
	};
}

console.log(
	"📱 FIXED AppApiService loaded - Production logout issues resolved",
);

export default appApiService;
